import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { ORG_CRM_READ_SCOPE } from "../auth/orgRoutes.ts";
import { userHasOrgScopeAnywhere } from "../auth/scopes.ts";
import CopyButton from "../components/CopyButton.tsx";
import PageHeader from "../components/PageHeader.tsx";
import SectionHeading from "../components/SectionHeading.tsx";
import { useAuth } from "../contexts/AuthContext.tsx";

const MCP_URL = "https://hive.sxq.no/mcp";
const CRM_MCP_URL = "https://hive.sxq.no/mcp/crm";

const claudeCliCommand = `claude mcp add scaleupxq --transport http ${MCP_URL}`;
const claudeCrmCliCommand = `claude mcp add hive-crm --transport http ${CRM_MCP_URL}`;

/** Monospace value with a copy button, used for connector URLs and CLI commands. */
function CopyableValue({ label, value }: { label: string; value: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 4, maxWidth: 720 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant="body1"
          sx={{ fontFamily: "monospace", fontWeight: 600, wordBreak: "break-all" }}
        >
          {value}
        </Typography>
        <CopyButton text={value} sx={{ mt: -0.5 }} />
      </Box>
    </Paper>
  );
}

function McpGuidePage() {
  const { user } = useAuth();
  // The CRM server is served only to CRM readers (and global admins); hide it
  // from everyone else rather than advertise a connector that would 403.
  const canReadCrm = userHasOrgScopeAnywhere(user, ORG_CRM_READ_SCOPE);
  return (
    <>
      <PageHeader
        title="Hive MCP"
        description={
          <>
            Connect AI tools to Hive as a connector — search companies, browse job ads, inspect
            technologies and financials, and manage collections (prospect lists)
          </>
        }
      />
      <CopyableValue label="Connector URL" value={MCP_URL} />
      <Alert severity="info" sx={{ mb: 4, maxWidth: 720 }}>
        Authentication is handled automatically via OAuth 2.1. When you first
        connect, your connector will open a browser window where you log in
        with your Hive credentials. Tokens are refreshed automatically.
      </Alert>
      {/* Claude Code */}
      <SectionHeading title="Claude Code" />
      <CopyableValue label="CLI command" value={claudeCliCommand} />
      {canReadCrm && (
        <>
          {/* Hive CRM */}
          <SectionHeading title="Hive CRM" />
          <Typography variant="body2" sx={{ mb: 2, maxWidth: 720, color: "text.secondary" }}>
            Read-only reporting over CRM data: tenants, BDRs, channels (calls, email, LinkedIn),
            call outcomes, funnels, drill-down into activities. Requires the CRM reader role in
            your organization.
          </Typography>
          <CopyableValue label="Connector URL" value={CRM_MCP_URL} />
          <CopyableValue label="Claude Code CLI command" value={claudeCrmCliCommand} />
        </>
      )}
    </>
  );
}

export default McpGuidePage;
